import styled, { css } from 'styled-components';
import { ThemeProps } from '@/styles/global';
import { StylesProps } from '@/utils/stylesProps';

type Props = StylesProps & ThemeProps;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${(props: ThemeProps) => props.theme.whiteColor};
  box-shadow: 0 0 0.25rem rgba(89, 115, 114, 0.16);
  border-radius: 0.25rem;
  padding: 4rem 7.5rem;
  margin: 1.25rem 0 2.5rem;
  width: 100%;
  @media (max-width: 768px) {
    padding: 2rem 1rem;
    margin: 1rem 0 1.5rem;
  }
`;

export const Title = styled.h2`
  margin: 0 0 1.4375rem;
  font-weight: 700;
  font-size: 2rem;
  line-height: 2.625rem;
  color: ${(props: ThemeProps) => props.theme.blackColor};
  text-align: center;
  @media (max-width: 768px) {
    margin: 0 0 1rem;
    font-size: 1.5rem;
  }
`;

export const Subtitle = styled.h3`
  font-weight: normal;
  font-size: 1.25rem;
  line-height: 1.75rem;
  color: ${(props: ThemeProps) => props.theme.blackColor};
  margin: 0 0 2.5rem;
  text-align: center;
  @media (max-width: 768px) {
    margin: 0 0 1rem;
    font-size: 1rem;
  }
`;

export const InputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  color: ${(props: ThemeProps) => props.theme.blackColor};
  & label {
    font-weight: 500;
    font-size: 1.125rem;
    line-height: 1.6875rem;
    margin: 2rem 0 1rem;
    & span {
      font-weight: normal;
    }
  }
  & input[type='checkbox'] + label {
    font-weight: 300;
    font-size: 1rem;
    line-height: 1.5rem;
    margin-bottom: 0.25rem;
  }
  & input[type='file'] {
    border: 0.0625rem solid ${(props: ThemeProps) => props.theme.lightGrayColor};
    border-radius: 0.25rem;
    font-weight: normal;
    font-size: 1.125rem;
    line-height: 1.6875rem;
    height: 3.5rem;
    outline: none;
    padding: 0.5rem;
    &::-webkit-file-upload-button {
      background-color: ${(props: ThemeProps) => props.theme.softGreenColor};
      border-radius: 0.25rem;
      font-style: normal;
      font-weight: 700;
      font-size: 1rem;
      line-height: 1.5rem;
      color: ${(props: ThemeProps) => props.theme.whiteColor};
      cursor: pointer;
      border: none;
      box-shadow: 0 0 1.5rem rgba(19, 187, 136, 0.2);
      &:hover {
        box-shadow: 0 0 1rem rgba(19, 187, 136, 0.4);
      }
      &:active {
        box-shadow: 0 0 1.5rem rgba(19, 187, 136, 0.2);
        transform: translateY(0.0625rem);
      }
      margin-right: 1.5rem;
      width: 9.5625rem;
      height: 100%;
    }
    &:focus {
      border-color: ${(props: ThemeProps) => props.theme.softGreenColor};
      box-shadow: 0 0 0.25rem rgba(89, 115, 114, 0.16);
    }
  }
  & hr {
    width: 100%;
  }
  @media (max-width: 768px) {
    & label {
      font-size: 1rem;
      margin: 1rem 0 1rem;
    }
    & input[type='file'] {
      font-size: 1rem;
      &::-webkit-file-upload-button {
        font-size: 0.75rem;
        width: 7rem;
      }
    }
    & textarea {
      font-size: 1rem;
    }
  }
`;

export const StyledButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const ButtonWrapper = styled.div`
  width: 14.8125rem;
  height: 3.5rem;
  margin-top: 2.5rem;
  align-self: end;
  @media (max-width: 768px) {
    width: 100%;
    height: 3rem;
    margin-top: 1rem;
  }
`;

export const StyledButton = styled.button`
  height: 3.5rem;
  margin-top: 2.5rem;
  font-weight: bold;
  font-size: 1.125rem;
  line-height: 1.6875rem;
  padding: 0 2rem;
  background-color: transparent;
  outline: none;
  color: ${(props: ThemeProps) => props.theme.softGreenColor};
  border: 0.0625rem solid ${(props: ThemeProps) => props.theme.softGreenColor};
  border-radius: 0.25rem;
  cursor: pointer;
  margin-right: 2rem;
  &:hover {
    box-shadow: 0 0 1rem rgba(19, 187, 136, 0.2);
  }
  &:active {
    box-shadow: 0 0 1.5rem rgba(19, 187, 136, 0.2);
    transform: translateY(0.0625rem);
  }
  @media (max-width: 768px) {
    width: 100%;
    height: 3rem;
    font-size: 1rem;
    padding: 0 0.5rem;
    line-height: normal;
    margin-top: 2rem;
  }
`;

export const StyledCompensationWrapper = styled.div`
  display: flex;
  margin-top: 2rem;
  & input {
    margin-top: 1rem;
    width: 100%;
  }
  & div:first-child {
    margin-right: 1rem;
  }
  & div:last-child {
    margin-left: 1rem;
  }
`;

export const StyledUl = styled.ul`
  display: flex;
  flex-wrap: wrap;
  padding: 0;
  margin: 8px 0 0 0;
  & li {
    width: auto;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${(props: ThemeProps) => props.theme.softGreenColor};
    padding: 0 0.5rem;
    font-weight: normal;
    font-size: 0.875rem;
    list-style: none;
    border-radius: 0.125rem;
    margin: 0 0.5rem 0.5rem 0;
    background-color: rgba(16, 168, 35, 0.13);
    & button {
      background-color: transparent;
      border: none;
      outline: none;
      cursor: pointer;
      padding-right: 0;
    }
  }
`;

export const Input = styled.input`
  border: 0.0625rem solid ${(props: ThemeProps) => props.theme.lightGrayColor};
  border-radius: 0.25rem;
  font-weight: normal;
  font-size: 1.125rem;
  line-height: 1.6875rem;
  height: 3.5rem;
  padding: 0 1.5rem;
  outline: none;
  &:focus {
    border-color: ${(props: ThemeProps) => props.theme.softGreenColor};
    box-shadow: 0 0 0.25rem rgba(89, 115, 114, 0.16);
  }
  ${(props: Props) =>
    props.isEmpty &&
    css`
      border-color: red !important;
      &::-webkit-input-placeholder {
        color: red;
      }
    `}
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 1rem;
    height: 3rem;
  }
`;

export const Textarea = styled.textarea`
  border: 0.0625rem solid ${(props: ThemeProps) => props.theme.lightGrayColor};
  border-radius: 0.25rem;
  font-weight: normal;
  font-size: 1.125rem;
  line-height: 1.6875rem;
  resize: vertical;
  width: 100% !important;
  min-height: 10.1875rem !important;
  padding: 0.875rem 1.5rem;
  outline: none;
  &:focus {
    border-color: ${(props: ThemeProps) => props.theme.softGreenColor};
    box-shadow: 0 0 0.25rem rgba(89, 115, 114, 0.16);
  }
  ${(props: Props) =>
    props.isEmpty &&
    css`
      border-color: red !important;
      &::-webkit-input-placeholder {
        color: red;
      }
    `}
  @media (max-width: 768px) {
    padding: 0.875rem 1rem;
  }
`;
